<template>
  <v-dialog
    :value="value"
    max-width="500px"
    @input="$emit('input', $event)"
  >
    <v-card>
      <v-card-title>
        <span class="text-h5">Edit User</span>
      </v-card-title>

      <v-card-text>
        <v-container>
          <v-row>
            <v-col cols="12">
              <v-text-field
                v-model="editedName"
                label="Name"
                :rules="nameRules"
                required
                outlined
                dense
              />
            </v-col>
            <v-col cols="12">
              <v-text-field
                v-model="editedEmail"
                label="Email"
                :rules="emailRules"
                type="email"
                required
                outlined
                dense
                :disabled="isDeakinUser"
                :hint="
                  isDeakinUser
                    ? 'Deakin email addresses cannot be changed'
                    : ''
                "
                persistent-hint
              />
            </v-col>
            <v-col cols="12">
              <v-switch
                v-model="editedCanCreateReviews"
                data-testid="can-create-reviews-toggle"
                label="Can create reviews"
                hint="External accounts should usually be off; they can only access reviews they've been added to."
                persistent-hint
                dense
              />
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn color="grey darken-1" text @click="onCancel">
          Cancel
        </v-btn>
        <v-btn
          color="primary"
          :disabled="!isValid || !hasChanges"
          :loading="loading"
          @click="onSave"
        >
          Save
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { EMAIL_REGEX } from '@/modules';
import { Researcher } from '@/models';

export default {
  name: 'UserEditDialog',
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    user: {
      type: Researcher,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['input', 'save', 'cancel'],
  data: () => ({
    editedName: '',
    editedEmail: '',
    editedCanCreateReviews: false,
    nameRules: [(v) => !!v || 'Name is required'],
    emailRules: [
      (v) => !!v || 'Email is required',
      (v) => EMAIL_REGEX.test(v) || 'Email must be valid',
    ],
  }),
  computed: {
    isDeakinUser() {
      return this.user ? this.user.isDeakinUser() : false;
    },
    isValid() {
      return (
        this.editedName &&
        this.editedEmail &&
        EMAIL_REGEX.test(this.editedEmail)
      );
    },
    hasChanges() {
      if (!this.user) return false;
      return (
        this.editedName !== this.user.name ||
        this.editedEmail !== this.user.email ||
        this.editedCanCreateReviews !== this.user.canCreateReviews
      );
    },
  },
  watch: {
    value(newValue) {
      if (newValue && this.user) {
        this.editedName = this.user.name;
        this.editedEmail = this.user.email;
        this.editedCanCreateReviews = this.user.canCreateReviews;
      }
    },
    user: {
      immediate: true,
      handler(newUser) {
        if (newUser) {
          this.editedName = newUser.name;
          this.editedEmail = newUser.email;
          this.editedCanCreateReviews = newUser.canCreateReviews;
        }
      },
    },
  },
  methods: {
    onSave() {
      if (this.isValid && this.hasChanges) {
        const input = {
          id: this.user.id,
        };

        if (this.editedName !== this.user.name) {
          input.name = this.editedName;
        }
        if (
          this.editedEmail !== this.user.email &&
          !this.isDeakinUser
        ) {
          input.email = this.editedEmail;
        }
        if (
          this.editedCanCreateReviews !== this.user.canCreateReviews
        ) {
          input.canCreateReviews = this.editedCanCreateReviews;
        }

        this.$emit('save', input);
      }
    },
    onCancel() {
      this.$emit('cancel');
      this.$emit('input', false);
    },
  },
};
</script>
